import { Link } from '@tanstack/react-router'

import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import type { usePrepaid } from '@/features/billing/api'
import { statusBorderClass } from '@/features/billing/status'

interface PrepaidListProps {
  readonly query: ReturnType<typeof usePrepaid>
}

export function PrepaidList({ query }: PrepaidListProps) {
  if (query.isPending) return <SkeletonRows testId="prepaid-loading" rows={2} />

  if (query.isError && (query.data === undefined || query.isFetchNextPageError)) {
    return (
      <ErrorState
        testId="prepaid-error"
        retryTestId="prepaid-retry"
        error={query.error}
        onRetry={() => {
          void (query.isFetchNextPageError ? query.fetchNextPage() : query.refetch())
        }}
      />
    )
  }

  const prepaid = query.data
  if (prepaid.items.length === 0) return null

  return (
    <section data-testid="prepaid-list" className="border-border flex flex-col border-t">
      <div className="flex h-(--row-h) items-center justify-between gap-3 px-(--page-x)">
        <h2 className="text-section text-text font-(--font-weight-section)">Im Voraus bezahlt</h2>
        <span className="text-label text-muted">{prepaid.totals.count_display}</span>
      </div>
      <ul>
        {prepaid.items.map((item) => (
          <li key={item.contract.id} className="border-border border-t">
            <Link
              to="/objekt/$entityId"
              params={{ entityId: item.contract.id }}
              search={{ tab: undefined }}
              data-testid={`prepaid-row-${item.contract.id}`}
              className="hover:bg-bg flex min-h-(--tap-min) items-start justify-between gap-3 px-(--page-x) py-2 transition-colors duration-(--duration-fast) ease-(--ease-out)"
            >
              <span className="text-body text-text min-w-0 font-medium">{item.tenant.label} · {item.subject.label}</span>
              <span data-testid={`prepaid-until-${item.contract.id}`} className={`text-label text-text shrink-0 border-l-3 py-0.5 pl-2 ${statusBorderClass('settled')}`}>
                {item.paid_until_display}
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {query.hasNextPage && (
        <button
          type="button"
          data-testid="prepaid-more"
          disabled={query.isFetchingNextPage}
          onClick={() => {
            void query.fetchNextPage()
          }}
          className="border-border text-text border-t px-3 py-2"
        >
          {query.isFetchingNextPage ? 'Lädt …' : 'Mehr laden'}
        </button>
      )}
    </section>
  )
}
